/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * pickerBeacon — survives iOS Photos picker handoff.
 *
 * Operator P0 (May 20, 2026):
 *   "After selecting videos from iPhone Photos, every role lands in
 *    a visible persistent basket/import state. user is never dumped
 *    back to the start screen. staged files survive modal remount/
 *    reload. upload does not start until user confirms."
 *
 * Root cause being defended against:
 *   iOS Safari WKWebView occasionally suspends-and-evicts the page
 *   during the native Photos picker handoff. When the page comes
 *   back, the in-flight modal has unmounted, its `<input type="file">`
 *   is gone, and the picker's onChange never fires. The user lands
 *   on the surface they opened the modal from with no visible state.
 *
 * Defence:
 *   BEFORE we call `fileInputRef.current.click()`, we synchronously
 *   write a tiny beacon into sessionStorage with:
 *     • basket_id  — server-side basket identity to resume
 *     • source     — which entry surface opened the modal
 *     • role       — learner / pdi / adi (best-effort, for diagnostics)
 *     • route      — pathname at picker-open (for diagnostics)
 *     • at         — ms timestamp
 *
 *   On every parent-page mount AND every pageshow/visibility/focus
 *   event, the parent reads the beacon. If it is fresh (< 90s) AND
 *   the modal is currently closed, the parent re-opens the basket
 *   modal with `resumeBasketId = beacon.basket_id`. The modal
 *   itself reads + clears the beacon on its own mount so the loop
 *   self-terminates — no infinite reopen.
 *
 * Why sessionStorage:
 *   • Cleared on tab close — never leaks across sessions.
 *   • Survives WKWebView suspend/resume + same-tab navigation.
 *   • Not synced across tabs — exactly the scope we want.
 *
 * The beacon is platform-wide. Every entry point that mounts
 * `ClipBasketModal` (LessonReview, TrainingMode, WorkMode,
 * InstructorPendingRequestsCard, InstructorTrainingUploadModal)
 * MUST use `useBasketAutoReopen` so all five flows survive the
 * same iOS handoff edge. Doctrine guard:
 * /app/backend/tests/test_upload_picker_continuity.py.
 */

export const PICKER_BEACON_KEY = "ndg:picker-beacon:v1";
export const PICKER_BEACON_TTL_MS = 90_000;

function _safeWindow() {
  return typeof window !== "undefined" ? window : null;
}

function _safeSessionStorage() {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch (_) {
    return null;
  }
}

/**
 * writePickerBeacon — fired synchronously BEFORE the file input
 * click. Idempotent: writes wholly replace any previous beacon.
 *
 * @param {object} fields
 * @param {string} fields.basket_id  — required, the server-side basket id.
 * @param {string} fields.source     — required, free-form entry-surface tag.
 *                                     E.g. "lesson_review", "training_mode",
 *                                     "work_mode", "instructor_pending_requests",
 *                                     "instructor_training_upload".
 * @param {string} [fields.role]     — optional, learner | pdi | adi | unknown.
 */
export function writePickerBeacon({ basket_id, source, role }) {
  const ss = _safeSessionStorage();
  if (!ss || !basket_id || !source) return false;
  const win = _safeWindow();
  const route = win && win.location ? win.location.pathname.slice(0, 200) : null;
  try {
    ss.setItem(
      PICKER_BEACON_KEY,
      JSON.stringify({
        basket_id,
        source,
        role: role || "unknown",
        route,
        at: Date.now(),
      }),
    );
    // eslint-disable-next-line no-console
    console.warn("[picker-beacon] written", { basket_id, source, role, route });
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * readPickerBeacon — returns the beacon if it is fresh, else null.
 * Does NOT clear; callers decide when to clear (the modal clears
 * once it has the basket resumed; the parent clears if the beacon
 * is stale or unusable).
 */
export function readPickerBeacon() {
  const ss = _safeSessionStorage();
  if (!ss) return null;
  let raw;
  try {
    raw = ss.getItem(PICKER_BEACON_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return null;
  }
  if (!parsed || !parsed.basket_id || !parsed.source || !parsed.at) {
    return null;
  }
  const age = Date.now() - parsed.at;
  if (age < 0 || age > PICKER_BEACON_TTL_MS) {
    // Stale — clear opportunistically so it can't leak into
    // future sessions, then return null.
    clearPickerBeacon("stale");
    return null;
  }
  return { ...parsed, age_ms: age };
}

/**
 * clearPickerBeacon — call after the basket has successfully
 * resumed (or when the user explicitly cancels recovery).
 */
export function clearPickerBeacon(reason = "consumed") {
  const ss = _safeSessionStorage();
  if (!ss) return false;
  try {
    ss.removeItem(PICKER_BEACON_KEY);
    // eslint-disable-next-line no-console
    console.warn("[picker-beacon] cleared", { reason });
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * isPickerBeaconFresh — boolean helper for guards and tests.
 */
export function isPickerBeaconFresh() {
  return readPickerBeacon() !== null;
}
